import { useMemo } from 'react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { canPerformOffline, type OfflineTier } from '@/lib/offlineCategories';

/**
 * Hook de garde à utiliser dans les pages pour bloquer proprement
 * les actions qui nécessitent une connexion réseau.
 *
 * Usage :
 *   const { isBlocked, blockMessage, tier } = useOfflineGuard('visioconference', 'write');
 *   // isBlocked = true si hors-ligne et l'opération n'est pas autorisée
 *   // blockMessage = message à afficher à l'utilisateur
 */
export function useOfflineGuard(
  tableOrOperation: string,
  operationType: 'read' | 'write' = 'write',
): {
  isBlocked: boolean;
  blockMessage: string | null;
  tier: OfflineTier;
} {
  const { isOnline } = useOnlineStatus();

  return useMemo(() => {
    if (isOnline) {
      return { isBlocked: false, blockMessage: null, tier: 'FULL_OFFLINE' };
    }

    const result = canPerformOffline(tableOrOperation, operationType);
    return {
      isBlocked: !result.allowed,
      blockMessage: result.message || null,
      tier: result.tier,
    };
  }, [isOnline, tableOrOperation, operationType]);
}